import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button, ButtonLink } from "@/components/Button";
import {
  DetailList,
  DetailListSkeleton,
  type DetailGroup,
} from "@/components/DetailList";
import { LoadError } from "@/components/LoadError";
import { Modal, ModalTitle } from "@/components/Modal";
import { TextInput } from "@/components/TextInput";
import { StatusPill } from "@/components/StatusPill";
import { Toast, useToast } from "@/components/Toast";
import { oidcConfig } from "@/config";
import { useApi } from "@/lib/api";
import { prepareAvatar } from "@/lib/prepareAvatar";

interface AccountSecurity {
  twoFactorEnabled: boolean;
  passkeyCount: number;
  hasPassword: boolean;
  isSsoManaged: boolean;
  canSetPassword: boolean;
  signInWithPassword: boolean;
  signInWithPasskey: boolean;
  signInWithSso: boolean;
  localSignInAllowed: boolean;
}

function accountsUrl(path: string) {
  return `${oidcConfig.authority}/accounts/${path}?returnUrl=${encodeURIComponent(window.location.href)}`;
}

export function Profile() {
  const { user, accessToken, logout, setUserName, setUserAvatarId } = useAuth();
  const api = useApi();
  const [security, setSecurity] = useState<AccountSecurity | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const avatarInput = useRef<HTMLInputElement>(null);
  const { toast, showToast } = useToast();

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let cancelled = false;

    async function loadSecurity() {
      const [twoFactor, passkeys, password, signInMethods] = await Promise.all([
        api.get<{ enabled: boolean }>("/my-account/two-factor"),
        api.get<{ count: number }>("/my-account/passkeys"),
        api.get<{
          hasPassword: boolean;
          isSsoManaged: boolean;
          canSetPassword: boolean;
        }>("/my-account/password"),
        api.get<{
          password: boolean;
          passkey: boolean;
          sso: boolean;
          localSignInAllowed: boolean;
        }>("/my-account/sign-in-methods"),
      ]);

      if (cancelled) {
        return;
      }

      if (!twoFactor || !passkeys || !password || !signInMethods) {
        setFailed(true);

        return;
      }

      setFailed(false);
      setSecurity({
        twoFactorEnabled: twoFactor.enabled,
        passkeyCount: passkeys.count,
        hasPassword: password.hasPassword,
        isSsoManaged: password.isSsoManaged,
        canSetPassword: password.canSetPassword,
        signInWithPassword: signInMethods.password,
        signInWithPasskey: signInMethods.passkey,
        signInWithSso: signInMethods.sso,
        localSignInAllowed: signInMethods.localSignInAllowed,
      });
    }

    void loadSecurity();

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, attempt]);

  async function handleAvatarChosen(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    setSavingAvatar(true);

    try {
      const image = await prepareAvatar(file);
      const { avatarId } = await api.send<{ avatarId: string }>(
        "/my-account/avatar",
        "PUT",
        image,
        "Couldn't save your profile picture.",
      );

      setUserAvatarId(avatarId);
      showToast("Profile picture updated.", "success");
    } catch (cause: unknown) {
      showToast(
        cause instanceof Error
          ? cause.message
          : "Couldn't save your profile picture.",
        "error",
      );
    } finally {
      setSavingAvatar(false);
    }
  }

  async function handleAvatarRemoved() {
    setSavingAvatar(true);

    try {
      await api.send(
        "/my-account/avatar",
        "DELETE",
        undefined,
        "Couldn't remove your profile picture.",
      );

      setUserAvatarId(null);
      showToast("Profile picture removed.", "success");
    } catch (cause: unknown) {
      showToast(
        cause instanceof Error
          ? cause.message
          : "Couldn't remove your profile picture.",
        "error",
      );
    } finally {
      setSavingAvatar(false);
    }
  }

  const isSso = security?.isSsoManaged ?? false;
  const managedByIdp = security ? !security.localSignInAllowed : false;
  const twoFactorManagedByIdpNote = managedByIdp
    ? "Two-factor authentication can be managed by your identity provider."
    : undefined;
  const signInMethods = [
    security?.signInWithPassword && "email and password",
    security?.signInWithPasskey && "passkey",
    security?.signInWithSso && "single sign-on",
  ]
    .filter(Boolean)
    .join(", ");

  const groups: DetailGroup[] = [
    {
      title: "Account",
      rows: [
        {
          label: "Name",
          value: (
            <>
              {user?.name ?? (
                <span className="text-foreground/70">Not set</span>
              )}
              {isSso && (
                <span className="block text-xs text-foreground/70">
                  Set by your identity provider
                </span>
              )}
            </>
          ),
          action: !isSso && (
            <Button
              size="sm"
              aria-label="Change name"
              onClick={() => setEditingName(true)}
            >
              Change
            </Button>
          ),
        },
        { label: "Email", value: user?.email ?? "Unknown" },
        { label: "Organization", value: user?.orgName ?? "Unknown" },
        {
          label: "Role",
          value: user?.roles[0] ?? "Unknown",
        },
        {
          label: "Signs in with",
          value:
            signInMethods.length > 0 ? (
              signInMethods.charAt(0).toUpperCase() + signInMethods.slice(1)
            ) : (
              <span className="text-foreground/70">None</span>
            ),
        },
      ],
    },
    {
      title: "Security",
      rows: [
        {
          label: "Password",
          value: <SetPill set={security?.hasPassword ?? false} />,
          action: (managedByIdp ||
            security?.hasPassword ||
            security?.canSetPassword) && (
            <SecurityAction
              label={security?.hasPassword ? "Change" : "Set up"}
              ariaLabel={
                security?.hasPassword ? "Change password" : "Set up password"
              }
              href={accountsUrl(
                security?.hasPassword ? "password/change" : "password",
              )}
              managedByIdpNote={
                managedByIdp
                  ? "Your password can be managed by your identity provider."
                  : undefined
              }
            />
          ),
        },
        {
          label: "Two-factor authentication",
          value: <SetPill set={security?.twoFactorEnabled ?? false} />,
          action: security?.twoFactorEnabled ? (
            <div className="flex gap-2">
              <SecurityAction
                label="Regenerate"
                ariaLabel="Regenerate recovery codes"
                href={accountsUrl("two-factor/recovery-codes/regenerate")}
                managedByIdpNote={twoFactorManagedByIdpNote}
              />
              <SecurityAction
                label="Disable"
                ariaLabel="Disable two-factor authentication"
                href={accountsUrl("two-factor/disable")}
                variant="dangerOutline"
                managedByIdpNote={twoFactorManagedByIdpNote}
              />
            </div>
          ) : (
            <SecurityAction
              label="Set up"
              ariaLabel="Set up two-factor authentication"
              href={accountsUrl("two-factor/setup")}
              managedByIdpNote={twoFactorManagedByIdpNote}
            />
          ),
        },
        {
          label: "Passkeys",
          value: <SetPill set={(security?.passkeyCount ?? 0) > 0} />,
          action: (
            <SecurityAction
              label={security?.passkeyCount ? "Change" : "Set up"}
              ariaLabel={
                security?.passkeyCount ? "Change passkeys" : "Set up passkeys"
              }
              href={accountsUrl(
                security?.passkeyCount ? "passkeys" : "passkeys/add",
              )}
              managedByIdpNote={
                managedByIdp
                  ? "Passkeys can be managed by your identity provider."
                  : undefined
              }
            />
          ),
        },
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-3xl text-foreground">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="mt-0.5 text-sm text-foreground/70">
          Your profile in the {user?.orgName} organization
        </p>
      </div>

      <div className="mb-6 flex items-center gap-4">
        <Avatar
          name={user?.name ?? null}
          email={user?.email ?? null}
          avatarId={user?.avatarId}
          size="xl"
        />
        <div className="min-w-0">
          <p className="text-lg font-semibold">
            {user?.name ?? user?.email ?? "Unknown"}
          </p>
          <p className="truncate text-sm text-foreground/70">
            {user?.email ?? "Unknown"}
          </p>
          <div className="mt-2 flex gap-2">
            <input
              ref={avatarInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void handleAvatarChosen(event)}
            />
            <Button
              size="sm"
              aria-label={savingAvatar ? undefined : "Change picture"}
              disabled={savingAvatar}
              onClick={() => avatarInput.current?.click()}
            >
              {savingAvatar ? "Saving..." : "Change"}
            </Button>
            {user?.avatarId && (
              <Button
                size="sm"
                aria-label="Remove picture"
                disabled={savingAvatar}
                onClick={() => void handleAvatarRemoved()}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {failed ? (
        <LoadError
          what="your security settings"
          onRetry={() => setAttempt((previous) => previous + 1)}
        />
      ) : !security ? (
        <DetailListSkeleton rows={5} />
      ) : (
        <DetailList groups={groups} />
      )}

      {editingName && (
        <ChangeNameModal
          currentName={user?.name ?? ""}
          onSaved={(name) => {
            setUserName(name);
            setEditingName(false);
            showToast(`Name changed to ${name}.`, "success");
          }}
          onClose={() => setEditingName(false)}
        />
      )}

      <div className="mt-6 flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Sign out</h2>
          <p className="mt-0.5 max-w-md text-xs text-foreground/70">
            Signing out will end your session on this device. You'll need to
            sign in again to access Tendra.
          </p>
        </div>
        <Button variant="dangerOutline" onClick={logout} className="shrink-0">
          Sign out
        </Button>
      </div>

      <Toast toast={toast} />
    </div>
  );
}

function SetPill({ set }: { set: boolean }) {
  return set ? (
    <StatusPill variant="active">Set</StatusPill>
  ) : (
    <StatusPill variant="suspended">Not set</StatusPill>
  );
}

function SecurityAction({
  label,
  ariaLabel,
  href,
  variant = "secondary",
  managedByIdpNote,
}: {
  label: string;
  ariaLabel: string;
  href: string;
  variant?: "secondary" | "dangerOutline";
  managedByIdpNote: string | undefined;
}) {
  if (managedByIdpNote) {
    return (
      <span title={managedByIdpNote}>
        <Button size="sm" variant={variant} aria-label={ariaLabel} disabled>
          {label}
        </Button>
      </span>
    );
  }

  return (
    <ButtonLink size="sm" variant={variant} aria-label={ariaLabel} href={href}>
      {label}
    </ButtonLink>
  );
}

function ChangeNameModal({
  currentName,
  onSaved,
  onClose,
}: {
  currentName: string;
  onSaved: (name: string) => void;
  onClose: () => void;
}) {
  const api = useApi();
  const [name, setName] = useState(currentName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const trimmed = name.trim();

    try {
      await api.send("/my-account/name", "PUT", { name: trimmed });
      onSaved(trimmed);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Couldn't save.");
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <ModalTitle>Change your name</ModalTitle>

        <p className="mt-1 text-sm text-foreground/70">
          This is how your name appears throughout the organization.
        </p>

        <label
          htmlFor="profileName"
          className="mb-1.5 mt-4 block text-sm text-foreground"
        >
          Name
        </label>
        <TextInput
          id="profileName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
          autoFocus
          className="w-full"
        />

        {error && <p className="mt-2 text-sm text-danger">{error}</p>}

        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || name.trim().length === 0}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
